import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useVerifyEmailMutation, useResendVerifyEmailMutation } from '../../features/auth/authApi';
import { otpSchema } from '../../lib/validators/authSchemas';
import { useResendCooldown } from '../../lib/useResendCooldown';
import { authErrorMessage } from '../../lib/authErrorMessage';
import OtpInput from '../../components/auth/OtpInput';
import Button from '../../components/ui/Button';

export default function VerifyEmail() {
  const location = useLocation();
  const email = location.state?.email;

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [justResent, setJustResent] = useState(false);

  const [verifyEmail, { isLoading: verifying }] = useVerifyEmailMutation();
  const [resendVerifyEmail, { isLoading: resending }] = useResendVerifyEmailMutation();
  const { remaining, start, canResend } = useResendCooldown(60);

  useEffect(() => {
    if (!email) return;
    start();
    // Arrived here because login rejected an unverified account — the
    // original registration code may well have expired by now, so get a
    // fresh one waiting rather than make them ask for it themselves.
    if (location.state?.autoResend) {
      resendVerifyEmail({ email })
        .unwrap()
        .then(() => setJustResent(true))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  if (!email) {
    return (
      <div className="text-center">
        <p className="text-sm text-foreground-muted mb-4">
          We couldn't find a registration in progress. Please register again.
        </p>
        <Link to="/register"><Button className="w-full">Back to registration</Button></Link>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="text-center">
        <p className="text-4xl mb-3">✅</p>
        <h1 className="text-xl font-semibold text-foreground mb-2">Email verified</h1>
        <p className="text-sm text-foreground-muted mb-6">You're all set — log in to continue.</p>
        <Link to="/login"><Button className="w-full">Go to login</Button></Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const parsed = otpSchema.safeParse({ otp });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    try {
      await verifyEmail({ email, otp }).unwrap();
      setVerified(true);
    } catch (err) {
      if (err?.data?.errors?.otp?.[0]) {
        setError(err.data.errors.otp[0]);
        return;
      }
      const status = err?.status;
      setError(
        authErrorMessage(err, {
          fallback: status === 410 ? 'That code has expired — request a new one.' : 'Invalid code — check it and try again.',
          notFound: "Email verification isn't available right now — please try again shortly, or contact support if this keeps happening.",
        })
      );
    }
  };

  const onResend = async () => {
    setError('');
    try {
      await resendVerifyEmail({ email }).unwrap();
      setOtp('');
      start();
    } catch (err) {
      setError(authErrorMessage(err, { fallback: 'Could not resend code.' }));
    }
  };

  return (
    <div className="text-center">
      <h1 className="text-xl font-semibold text-foreground mb-1">Verify your email</h1>
      <p className="text-sm text-foreground-muted mb-6">
        Enter the 6-digit code we sent to <span className="text-foreground">{email}</span>.
      </p>

      {justResent && (
        <p className="text-sm text-accent bg-accent-soft rounded-lg px-3.5 py-2.5 mb-4">
          We've sent a fresh code to your inbox.
        </p>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <OtpInput value={otp} onChange={setOtp} error={error} />

        <Button type="submit" className="w-full" isLoading={verifying}>
          Verify email
        </Button>
      </form>

      <p className="text-sm text-foreground-muted mt-6">
        Didn't get a code?{' '}
        <button
          type="button"
          onClick={onResend}
          disabled={!canResend || resending}
          className="text-accent hover:underline disabled:text-foreground-subtle disabled:no-underline disabled:cursor-not-allowed"
        >
          {canResend ? 'Resend code' : `Resend in ${remaining}s`}
        </button>
      </p>

      <p className="text-sm text-foreground-muted mt-4">
        <Link to="/login" className="text-accent hover:underline">Back to login</Link>
      </p>
    </div>
  );
}
